import { useMemo, useRef } from "react"
import { Bound } from "lib/@components/binding/Bound"
import { useParams } from "react-router"
import { ComparisonView } from "routes/schedule/compare/comparisonView"
import { useCompareSchedule } from "./lib/use-compare-schedule"
import { refreshOnlyFor } from "lib/graphql/api"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { getScheduleDraft } from "./controller/get-schedule-draft"
import { indexAuthoringTasksInSchedule } from "./lib/index-authoring-tasks-in-schedule"
import noop from "lib/noop"
import { ProjectScheduleCrumbs } from "../components/project-schedule-crumbs"
import { If } from "lib/@components/switch"
import { Secure } from "lib/authorization/secure"
import { DraftStatus } from "authoring/lib/enum/draft-status.mjs"
import { ConditionalRedirect } from "lib/@components/conditional-redirect"
import { AuthoringAmendsType } from "../../../lib/enum/authoring-amends-type.mjs"
import { authoringReviewDecisions } from "./authoring-review-decisions"
import { getDraftAmendsByReferenceIdAndType } from "../amends/controller/get-draft-amends-by-reference-id-and-type"
import { ScheduleHeaderToolbar, UOMArea } from "slot-definitions"
import { replaceAllWith } from "lib/@components/slot/replace-all-with"
import { ApproveReviewButton } from "./plugins/review/approve-review-button"
import { AuthoringReviewComparisonTypeContent } from "./authoring-review-comparison-type-content"
import { AuthoringReviewBothPanel } from "./authoring-review-both-panel"
import { BothPanelWrapper } from "routes/schedule/compare/compare-two-impl"
import { ComparisonTypeContentWrapper } from "routes/schedule/compare/comparisonType"
import "./code-comparitor"
import { RejectReviewButton } from "./plugins/review/reject-review-button"
import { Box, Stack } from "@mui/material"
import { useDraftAmends } from "./lib/use-draft-amends"
import { AmendsBoundary } from "../amends/components/amends-boundary"
import { useEnrichedMacroMap } from "authoring/routes/macro/hooks/use-enriched-macro-map"
import {
    collectMacroTokensFromSchedule,
    extractMacroTokensFromString,
} from "authoring/routes/macro/lib/macro-token-utils"
import { buildScheduleContent } from "authoring/routes/macro/lib/macro-conversion"
import { normalizeScheduleHtml } from "./lib/normalize-schedule-html"
import { ScheduleMacroAutocomplete } from "authoring/routes/project/schedule-editor/schedule-macro-autocomplete"
import { getExpandedSchedulePreview } from "./controller/get-expanded-schedule-preview"
import { withInitialValue } from "lib/graphql/with-initial-value"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { Redirect } from "lib/@components/redirect"
import { getAudienceAmendsContents } from "../amends/lib/audience-aware-amends.mjs"
import { prepareScheduleForAuthoringComparison } from "./lib/prepare-schedule-for-authoring-comparison"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { SequentialReviewProvider } from "./plugins/review/sequential-review-provider"
import { SequentialReviewSidebar } from "./plugins/review/sequential-review-sidebar"
import { AUTHORING_REVIEW_SCHEDULES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { buildAmendsReferenceId } from "authoring/routes/project/amends/lib/amends-utils.mjs"
import { SeedSource } from "authoring/lib/enum/seed-source.mjs"
import { Frag } from "lib/@components/slot/frag"
import { useDraftScheduleReviewDecisionByAmendsKey } from "./lib/use-draft-schedule-review-decision-by-amends-key"

export function ReviewScheduleDraft() {
    ComparisonTypeContentWrapper.usePlug(replaceAllWith(<AuthoringReviewComparisonTypeContent />))
    BothPanelWrapper.usePlug(replaceAllWith(<AuthoringReviewBothPanel />))
    UOMArea.usePlug(replaceAllWith(<Frag />))
    ScheduleHeaderToolbar.usePlug(
        replaceAllWith(
            <Stack direction="row" spacing={1}>
                <ApproveReviewButton /> <RejectReviewButton />
            </Stack>
        )
    )

    const [sequential] = useSearchParam("sequential", "")
    const isSequentialReview = sequential === "true"

    const { scheduleId: source, projectId } = useParams()
    const draftFilter = refreshOnlyFor(createDraftId(projectId, source))
    const scheduleDraftResult = getScheduleDraft.useResults(
        { projectId, scheduleId: source },
        draftFilter,
        withInitialValue(IS_LOADING)
    )
    const loadingDestination = scheduleDraftResult === IS_LOADING
    const authoringDraftData = loadingDestination ? undefined : scheduleDraftResult
    const isNewSchedule = authoringDraftData?.seedSource === SeedSource.NEW
    const { loading: loadingSource, schedule: scheduleSource } = useCompareSchedule(
        source,
        "no_v",
        !!authoringDraftData && !isNewSchedule
    )
    const { id: draftId, reviewCycle, status } = authoringDraftData ?? {}
    const submitted = status === DraftStatus?.SUBMITTED
    const { amends, saveAmend, deleteAmend } = useDraftAmends({
        projectId,
        scheduleId: source,
        seedSource: authoringDraftData?.seedSource,
    })
    const amendsWithKeys = amends?.filter((amend) => amend?.payload?.amendsKey)

    const rejectionsByAmendsKey = useDraftScheduleReviewDecisionByAmendsKey({
        projectId,
        scheduleId: source,
        reviewCycle,
    })

    // Server expands both linked fields (componentRef/linkedFields → actual values)
    // and macro tokens, so the comparison shows real content differences.
    const expandedPreviewData = getExpandedSchedulePreview.useResults(
        { projectId, scheduleId: source },
        draftFilter,
        withInitialValue(null)
    )
    const expandedDestination = expandedPreviewData?.schedule

    const tokenIds = useMemo(collectTokenIds, [expandedDestination, scheduleSource, amendsWithKeys])
    const { macros, macroMap, loading: macrosLoading } = useEnrichedMacroMap({ tokenIds, retrieveDeleted: false })
    const displayDestination = useMemo(prepareDestination, [expandedDestination])
    const displaySource = useMemo(prepareSource, [scheduleSource, macroMap, isNewSchedule, source])
    if (displaySource?.tasks?.length) indexAuthoringTasksInSchedule(displaySource)
    if (displayDestination?.tasks?.length) indexAuthoringTasksInSchedule(displayDestination)

    const { title } = displayDestination || {}
    const reviewReferenceId = buildAmendsReferenceId({ id: draftId, reviewCycle })
    // Load every task's decision once for the whole review, keyed by groupReviewId. Each inline
    // button reads its decision from this lookup (see authoring-review-decisions.useDecision)
    // instead of firing its own query — so a single approve invalidates one shared query, not N.
    const decisionRows = getDraftAmendsByReferenceIdAndType.useResults(
        reviewReferenceId,
        AuthoringAmendsType.AUTHORING_AMENDS,
        { "payload.groupReviewId": reviewReferenceId },
        projectId,
        draftFilter
    )
    const decisionsByTaskId = useMemo(buildDecisionsByTaskId, [decisionRows])
    const reviewDecisions = useMemo(
        () => authoringReviewDecisions({ projectId, scheduleId: source, reviewCycle, decisionsByTaskId }),
        [projectId, source, reviewCycle, decisionsByTaskId]
    )

    // Memoised so a decisions-only refetch (which changes reviewDecisions but not the schedule
    // content) keeps the same object identity. CompareTwo re-clones its `target` whenever this
    // object changes, which remounts the whole comparison tree and resets the inline buttons'
    // optimistic state — the cause of the approve/approved flash.
    const reviewScheduleDestination = useMemo(
        () => displayDestination && { ...displayDestination, groupReviewId: reviewReferenceId, status },
        [displayDestination, reviewReferenceId, status]
    )

    const hasRenderedOnceRef = useRef(false)
    const redirectPath = `/authoring/projects/${projectId}`

    // First-load gate ONLY. Every inline button must mount with its decision already present
    // (it hydrates local state once and never re-syncs), so we wait for decisions on first load.
    // But once we've rendered, a later refetch must NOT blank the page — returning null again
    // would unmount and remount the whole comparison tree and flick the buttons.
    const initialising =
        loadingDestination ||
        (!isNewSchedule && loadingSource) ||
        !expandedPreviewData ||
        macrosLoading ||
        decisionRows === undefined
    if (!initialising) hasRenderedOnceRef.current = true
    if (initialising && !hasRenderedOnceRef.current) {
        return null
    }

    const reviewContent = (
        <Secure demand={AUTHORING_REVIEW_SCHEDULES_DEMAND} fallback={<Redirect to={redirectPath} />}>
            <ConditionalRedirect when={!submitted} to={redirectPath}>
                <If truthy={!!displayDestination && !!displaySource}>
                    <Bound
                        comparisonReadOnly
                        compareFormatting
                        hasAcceptReject
                        Crumbs={ProjectScheduleCrumbs}
                        hideUpBar={isSequentialReview}
                        isAuthoringScheduleEditor
                        isAuthoringPdfExportSource={true}
                        projectId={projectId}
                        scheduleId={source}
                        reviewCycle={reviewCycle}
                        seedSource={authoringDraftData?.seedSource}
                        reviewReferenceId={reviewReferenceId}
                        reviewDecisions={reviewDecisions}
                        splitDetailsCompareButtons
                        splitIntroCompareButtons
                        macroMap={macroMap}
                        rejectionsByAmendsKey={rejectionsByAmendsKey}
                    >
                        <AmendsBoundary amends={amendsWithKeys} onSaveAmend={saveAmend} onDeleteAmend={deleteAmend}>
                            <ScheduleMacroAutocomplete macros={macros} />
                            <Bound
                                comparisonReadOnly
                                hasAcceptReject
                                Crumbs={ProjectScheduleCrumbs}
                                hideUpBar={isSequentialReview}
                                reviewReferenceId={reviewReferenceId}
                                reviewDecisions={reviewDecisions}
                                splitDetailsCompareButtons
                                splitIntroCompareButtons
                            >
                                <ComparisonView
                                    $lazy
                                    destination={[reviewScheduleDestination, noop, noop, `Draft of ${title}`]}
                                    source={[
                                        displaySource,
                                        noop,
                                        noop,
                                        isNewSchedule
                                            ? "No prior version"
                                            : `Last published version ${displaySource.version} of ${title}`,
                                    ]}
                                />
                            </Bound>
                        </AmendsBoundary>
                    </Bound>
                </If>
            </ConditionalRedirect>
        </Secure>
    )

    if (isSequentialReview) {
        return (
            <SequentialReviewProvider>
                <Stack direction="row" sx={{ height: "100%", flex: 1, minHeight: 0 }}>
                    <SequentialReviewSidebar />
                    <Box sx={{ flex: 1, overflowY: "auto" }}>{reviewContent}</Box>
                </Stack>
            </SequentialReviewProvider>
        )
    }

    return reviewContent

    function prepareDestination() {
        return prepareScheduleForAuthoringComparison(expandedDestination)
    }

    function prepareSource() {
        if (isNewSchedule) {
            return prepareScheduleForAuthoringComparison({
                _id: `${source}:empty-baseline`,
                id: source,
                code: "",
                title: "",
                version: "0",
                unitOfMeasure: "",
                intro: {
                    content: "",
                    note: "",
                },
                tasks: [],
                legislation: [],
                metadata: {
                    published: null,
                },
            })
        }
        return prepareScheduleForAuthoringComparison(
            normalizeScheduleHtml(buildScheduleContent(scheduleSource, macroMap))
        )
    }

    function collectTokenIds() {
        const tokens = new Set()
        collectMacroTokensFromSchedule(expandedDestination, tokens)
        if (scheduleSource) {
            collectMacroTokensFromSchedule(scheduleSource, tokens)
        }
        for (const amend of amendsWithKeys ?? []) {
            for (const content of getAudienceAmendsContents(amend?.payload)) {
                for (const tokenId of extractMacroTokensFromString(content)) {
                    tokens.add(tokenId)
                }
            }
        }
        return [...tokens]
    }

    function buildDecisionsByTaskId() {
        const map = new Map()
        for (const row of decisionRows ?? []) {
            const taskId = row?.payload?.taskId
            if (taskId != null && !map.has(taskId)) map.set(taskId, row)
        }
        return map
    }
}
